import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import { COLOR_PRIMARY, COLOR_DESTRUCTIVE, COLOR_DESTRUCTIVE_HOVER } from "../../styles/colors";
import {
  getSavedDesigns,
  loadDesign,
  deleteDesign,
  setCurrentDesignName,
  getCurrentDesignName,
} from "../../store";
import { loadDesignState } from "../../store/game/gameSlice";
import { useDispatch } from "react-redux";

interface MyDesignsModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a design is loaded so the parent can update its label. */
  onDesignLoaded: (name: string) => void;
}

const MyDesignsModal: React.FC<MyDesignsModalProps> = ({ open, onClose, onDesignLoaded }) => {
  const dispatch = useDispatch();
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [designs, setDesigns] = useState<string[]>([]);
  const currentDesign = getCurrentDesignName();

  // Refresh the list every time the dialog opens
  React.useEffect(() => {
    if (open) {
      setDesigns(getSavedDesigns());
      setDeleteTarget(null);
    }
  }, [open]);

  const handleLoad = (name: string) => {
    const state = loadDesign(name);
    if (state) {
      setCurrentDesignName(name);
      dispatch(loadDesignState(state));
      onDesignLoaded(name);
      onClose();
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deleteDesign(deleteTarget);
      setDesigns(getSavedDesigns());
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="my-designs-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="my-designs-dialog-title"
          sx={{ fontWeight: 700, color: COLOR_PRIMARY }}
        >
          My Designs
        </DialogTitle>
        <DialogContent>
          {designs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No saved designs yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Use the save button to save your current design.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {designs.map((name, idx) => (
                <React.Fragment key={name}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      py: 1.5,
                      px: 1,
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        flex: 1,
                        fontWeight: name === currentDesign ? 700 : 400,
                        color: name === currentDesign ? COLOR_PRIMARY : "text.primary",
                      }}
                    >
                      {name}
                      {name === currentDesign && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, color: COLOR_PRIMARY, fontWeight: 400 }}
                        >
                          (active)
                        </Typography>
                      )}
                    </Typography>
                    <IconButton
                      aria-label={`load ${name}`}
                      size="small"
                      onClick={() => handleLoad(name)}
                      sx={{
                        color: COLOR_PRIMARY,
                        mr: 0.5,
                        "&:hover": { backgroundColor: "rgba(21,118,152,0.08)" },
                      }}
                    >
                      <OpenInBrowserIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={`delete ${name}`}
                      size="small"
                      onClick={() => setDeleteTarget(name)}
                      sx={{
                        color: COLOR_DESTRUCTIVE,
                        "&:hover": { backgroundColor: "rgba(211,47,47,0.08)" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {idx < designs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        aria-labelledby="delete-confirm-dialog-title"
      >
        <DialogTitle id="delete-confirm-dialog-title" sx={{ fontWeight: 700 }}>
          Delete Design?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget}"</strong>?
            This action is <strong>irreversible</strong> and the design cannot be recovered.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: COLOR_DESTRUCTIVE,
              "&:hover": { backgroundColor: COLOR_DESTRUCTIVE_HOVER },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyDesignsModal;
